import {
    ClassProvider,
    EnvironmentProviders,
    FactoryProvider,
    Injectable,
    InjectionToken,
    Injector,
    ModuleWithProviders,
    NgModule,
    Provider,
    inject,
} from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";
import { NgxsStoragePlugin, NgxsStoragePluginModule, NgxsStoragePluginOptions, STORAGE_ENGINE, StorageOption } from "@ngxs/storage-plugin";
import { InitState, NGXS_PLUGINS, NgxsNextPluginFn, Store } from "@ngxs/store";
import { BehaviorSubject } from "rxjs";

const STORAGE_SCOPE_SELECTOR = new InjectionToken<(state: unknown) => string>("Storage scope selector");
const STORAGE_SCOPE_SUBJECT = new InjectionToken<BehaviorSubject<string | null>>("Storage scope subject");

function isClassOrFactoryProvider(provider: Provider | EnvironmentProviders): provider is ClassProvider | FactoryProvider {
    return "useClass" in provider || "useFactory" in provider;
}

@UntilDestroy()
@Injectable()
class NgxsScopedStoragePlugin extends NgxsStoragePlugin {
    private readonly injector = inject(Injector);
    private readonly storageScopeSubject = inject(STORAGE_SCOPE_SUBJECT);

    private scopeSelector: undefined | ((state: unknown) => string);
    private store: undefined | Store;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public override handle(state: any, event: any, next: NgxsNextPluginFn): any {
        this.scopeSelector ??= this.injector.get(STORAGE_SCOPE_SELECTOR);
        this.store ??= this.injector.get(Store);

        const scope = this.store.selectSnapshot(this.scopeSelector);

        if (scope && scope !== this.storageScopeSubject.value) {
            this.storageScopeSubject.next(scope);
            this.storageScopeSubject.complete();

            return super.handle(state, new InitState(), next);
        }

        return super.handle(state, event, next);
    }
}

function provideNgxsScopedStorageEngine<T>(scopeSelector: (state: T) => string, storageOption?: StorageOption): Provider[] {
    return [
        {
            provide: STORAGE_SCOPE_SELECTOR,
            useValue: scopeSelector,
        },
        {
            provide: STORAGE_SCOPE_SUBJECT,
            useValue: new BehaviorSubject<string | null>(null),
        },
        {
            provide: STORAGE_ENGINE,
            deps: [STORAGE_SCOPE_SUBJECT],
            useFactory: (storageScopeSubject: BehaviorSubject<string | null>) => {
                const storage = storageOption === StorageOption.SessionStorage ? sessionStorage : localStorage;

                return {
                    getItem: (key: string) => storage.getItem(storageScopeSubject.value + ":" + key),
                    setItem: (key: string, value: string) => {
                        const scope = storageScopeSubject.value;
                        if (scope) {
                            storage.setItem(scope + ":" + key, value);
                        }
                    },
                };
            },
        },
        {
            provide: NGXS_PLUGINS,
            useClass: NgxsScopedStoragePlugin,
            multi: true,
        },
    ];
}

@NgModule()
export class NgxsScopedStoragePluginModule {
    public static forRoot<T>(
        options: NgxsStoragePluginOptions & { scopeSelector: (state: T) => string }
    ): ModuleWithProviders<NgxsStoragePluginModule> {
        const module = NgxsStoragePluginModule.forRoot(options);

        module.providers ??= [] as (Provider | EnvironmentProviders)[];
        module.providers = module.providers.filter(
            (provider: Provider | EnvironmentProviders) =>
                !isClassOrFactoryProvider(provider) || (provider.provide !== NGXS_PLUGINS && provider.provide !== STORAGE_ENGINE)
        );
        module.providers.push(...provideNgxsScopedStorageEngine(options.scopeSelector, options.storage));

        return module;
    }
}
